import { useMemo } from 'react';
import { QueryClient } from 'react-query';
import {
  CaseAttributesType,
  CaseType,
  ReportStatus,
  useGetCaseAttributesQuery,
} from 'src/graphql';

export const useCaseStatus = (caseID?: string) => {
  const { data, refetch: refetchAttributes } = useGetCaseAttributesQuery(
    {
      caseId: caseID ?? '',
    },
    {
      enabled: !!caseID,
    },
  );

  let caseStatus = useMemo(
    () => getCaseStatusFromAttributes(data?.getCaseAttributes),
    [data?.getCaseAttributes],
  );

  return { status: caseStatus, refetchAttributes };
};

export function getCaseStatusFromAttributes(
  attributes: Partial<CaseAttributesType> | undefined | null,
) {
  const { closedAt, readAt, investigatingAt } = attributes ?? {};
  let caseStatus = ReportStatus.New;
  if (readAt) {
    caseStatus = ReportStatus.Read;
  }
  if (investigatingAt) {
    caseStatus = ReportStatus.Investigating;
  }
  if (closedAt) {
    caseStatus = ReportStatus.Closed;
  }
  return caseStatus;
}

export function getAttributesFromStatus(
  newCaseStatus: ReportStatus,
  attributes: Partial<CaseAttributesType> | undefined | null,
) {
  let newAttributes: Partial<CaseAttributesType> = {
    createdAt: attributes?.createdAt,
    case: attributes?.case,
  };
  if (newCaseStatus === ReportStatus.Read) {
    newAttributes.readAt = new Date().toISOString();
  }
  if (newCaseStatus === ReportStatus.Investigating) {
    newAttributes.readAt = attributes?.readAt;
    newAttributes.investigatingAt = new Date().toISOString();
  }
  if (newCaseStatus === ReportStatus.Closed) {
    newAttributes.readAt = attributes?.readAt;
    newAttributes.investigatingAt = attributes?.investigatingAt;
    newAttributes.closedAt = new Date().toISOString();
  }
  return newAttributes;
}

export const onMutateCaseStatus =
  ({ id, queryClient }: { id: string; queryClient: QueryClient }) =>
  ({ newCaseStatus }: { newCaseStatus: ReportStatus }) => {
    const data = queryClient.getQueryData<{ caseById: CaseType }>([
      'CaseById',
      { id },
    ]);

    const updatedCaseAttributes = getAttributesFromStatus(
      newCaseStatus,
      data?.caseById.CaseAttributes,
    );

    const updated = {
      ...data?.caseById,
      status: newCaseStatus,
      CaseAttributes: updatedCaseAttributes,
    };

    queryClient.setQueryData(['CaseById', { id }], () => ({
      caseById: updated,
    }));

    return { caseById: updated };
  };
